// ═══════════════════════════════════════════════════════════════════════════════
// AI Prodavac - Phase 3 Readiness Card
// Shows Learning Engine activation status
// ═══════════════════════════════════════════════════════════════════════════════

import { Sparkles, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface Phase3ReadinessCardProps {
  phase3: {
    totalConversations: number;
    successfulConversations: number;
    readyForLearning: boolean;
    conversationsNeeded: number;
    message: string;
  };
}

export function Phase3ReadinessCard({ phase3 }: Phase3ReadinessCardProps) {
  const progress = (phase3.successfulConversations / 50) * 100;
  const isReady = phase3.readyForLearning;

  return (
    <Card className={cn(
      'h-full',
      isReady && 'border-status-success/40 bg-status-success/5'
    )}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className={cn(
              'w-5 h-5',
              isReady ? 'text-status-success' : 'text-muted-foreground'
            )} />
            Phase 3: Learning Engine
          </CardTitle>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="min-h-11 min-w-11 p-0 md:min-h-11 md:min-w-11"
                  aria-label="About the Learning Engine"
                >
                  <Info className="w-4 h-4 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">
                  Learning Engine uses RAG (Retrieval-Augmented Generation) to learn from successful
                  conversations. Viktor will enhance his responses based on what works best.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Successful Conversations</span>
              <span className="font-semibold text-foreground">
                {phase3.successfulConversations} / 50
              </span>
            </div>
            <Progress 
              value={progress}
              aria-label="Learning Engine readiness"
              aria-valuetext={`${phase3.successfulConversations} of 50 successful conversations`}
              className={cn(
                'h-3',
                isReady && '[&>div]:bg-status-success'
              )}
            />
          </div>

          <div className={cn(
            'p-3 rounded-lg text-sm',
            isReady 
              ? 'border border-status-success/20 bg-status-success/10 text-status-success'
              : 'bg-surface text-muted-foreground'
          )}>
            {phase3.message}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant={isReady ? 'default' : 'outline'}
              size="sm"
              className={cn(
                'min-h-11 flex-1 md:min-h-11',
                isReady && 'bg-status-success text-white hover:bg-status-success/90'
              )}
              disabled={!isReady}
            >
              {isReady ? 'Activate Learning' : 'Not Ready Yet'}
            </Button>
          </div>

          {!isReady && (
            <p className="text-xs text-muted-foreground">
              Learning Engine will be available when Viktor has enough successful conversations to
              learn from ({phase3.conversationsNeeded} more needed).
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
